import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges,
  OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';

type AutocompleteSelection = string | { value: string; label: string; isCreated?: boolean } | null;

const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 8;
const MIN_SEARCH_LENGTH = 3;
/** Smallest useful list height (header + ~2 options); also the threshold for flipping to the other side. */
const MIN_PANEL_HEIGHT = 140;
/** Upper limit for the list height, even when more space is available. */
const MAX_PANEL_HEIGHT = 274;

/** Screen area (in viewport coordinates) the panel may occupy. */
interface ScreenArea {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

@Component({
  selector: 'app-autocomplete-modal',
  templateUrl: './chat-autocomplete-modal.component.html',
  styleUrls: ['./chat-autocomplete-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatAutocompleteModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedValue: string | null = '';
  @Input() displayValue: string | null = '';
  @Input() options: any[] = [];
  @Input() title: string = '';
  @Input() placeholder: string = '';
  @Input() labelKey: string = 'label';
  @Input() valueKey: string = 'value';
  @Input() allowCreate: boolean = false;
  @Input() createOptionLabel: string = 'Tap to create';
  @Input() selectOptionLabel: string = 'Tap to select';
  @Input() iconOffset: boolean = false;
  /** Preferred side for the suggestion list; flips automatically when there is not enough room. */
  @Input() popupDirection: 'top' | 'bottom' = 'top';
  @Output() selectionChanged = new EventEmitter<AutocompleteSelection>();

  @ViewChild('fieldRef', { static: true }) fieldRef!: ElementRef<HTMLElement>;
  @ViewChild('inputRef', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  searchTerm: string = '';
  filteredValues: any[] = [];
  isOpen = false;
  placement: 'top' | 'bottom' = 'top';
  panelStyle: { [key: string]: string } = {};
  private hasUnselectedInput = false;

  /** Repositions the open panel when the page scrolls, the window resizes or the on-screen keyboard toggles. */
  private readonly onViewportChange = () => {
    if (this.isOpen) {
      this.zone.run(() => this.updatePanelPosition());
    }
  };

  constructor(private readonly elementRef: ElementRef<HTMLElement>, private readonly zone: NgZone) { }

  /**
   * Prefills the input with the selected option's label, loads the option list and
   * registers viewport listeners outside Angular to avoid change detection on every scroll.
   */
  ngOnInit(): void {
    this.searchTerm = this.getSelectedLabel();
    this.filteredValues = this.options ?? [];
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onViewportChange, true);
      window.addEventListener('resize', this.onViewportChange);
      // The visual viewport shrinks/pans when the on-screen keyboard opens.
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
    });
  }

  /**
   * Syncs the input label with new selection inputs while the panel is closed (so typing is never overwritten)
   * and re-filters the list when options change while the panel is open.
   * @param changes The changed input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['displayValue'] || changes['selectedValue'] || changes['options']) && !this.isOpen) {
      this.searchTerm = this.getSelectedLabel();
    }
    if (changes['options'] && this.isOpen) {
      this.filterValues();
    }
  }

  /** Removes the viewport listeners registered in `ngOnInit`. */
  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
  }

  /**
   * Dismisses the panel when the user presses anywhere outside the component.
   * @param event The document-level mouse or touch event.
   */
  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  onDocumentPointerDown(event: Event) {
    if (this.elementRef.nativeElement.contains(event.target as Node)
      || (!this.isOpen && !this.hasUnselectedInput)) return;

    if (this.allowCreate && this.hasUnselectedInput && this.hasUnselectedText()) {
      this.createAndSelectValue();
    } else {
      this.cancel();
    }
  }

  /** Hint text shown under each selectable option. */
  get selectHint(): string {
    return this.selectOptionLabel;
  }

  /** Hint text shown under the "create new" option. */
  get createHint(): string {
    return this.createOptionLabel;
  }

  /**
  * Whether to offer creating a new entry: creation must be allowed, the search term must be
  * nonempty and must not exactly match (case-insensitive) an existing label.
   */
  get showCreateOption(): boolean {
    if (!this.allowCreate) return false;
    const normalizedSearch = this.searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return false;

    return !(this.options ?? []).some((item: any) =>
      item[this.labelKey]?.toString().toLowerCase() === normalizedSearch
    );
  }

  /** Opens the suggestion panel with the full option list and positions it next to the input. */
  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    // Show the full list on open; filtering kicks in once the user types.
    this.filteredValues = this.options ?? [];
    this.updatePanelPosition();
  }

  /** Opens or closes the panel from the chevron button; opening also focuses the input. */
  toggle() {
    if (this.isOpen) {
      this.cancel();
    } else if (this.hasMinimumSearchLength()) {
      this.inputRef.nativeElement.focus();
      this.open();
    }
  }

  /** Opens the panel after the minimum search length and filters by the current search term. */
  onInput() {
    this.hasUnselectedInput = true;
    if (!this.hasMinimumSearchLength()) {
      this.isOpen = false;
      return;
    }
    if (!this.isOpen) {
      this.isOpen = true;
      this.updatePanelPosition();
    }
    this.filterValues();
  }

  /** Checks if the current search term meets the minimum length requirement. */
  private hasMinimumSearchLength(): boolean {
    return this.searchTerm.trim().length >= MIN_SEARCH_LENGTH;
  }

  /** Checks if the current search term differs from the selected label. */
  private hasUnselectedText(): boolean {
    const typedLabel = this.searchTerm.trim().toLowerCase();
    return !!typedLabel && typedLabel !== this.getSelectedLabel().trim().toLowerCase();
  }

  /** Filters options whose label contains the trimmed search term (case-insensitive). */
  filterValues() {
    const normalizedSearch = this.searchTerm.trim().toLowerCase();
    this.filteredValues = (this.options ?? []).filter((item: any) =>
      item[this.labelKey]?.toString().toLowerCase().includes(normalizedSearch)
    );
  }

  /**
   * Commits an option: fills the input with its label, closes the panel and emits its value.
   * @param item The option chosen from the list.
   */
  selectItem(item: any) {
    this.selectedValue = item[this.valueKey];
    this.searchTerm = item[this.labelKey]?.toString() ?? '';
    this.hasUnselectedInput = false;
    this.closePanel();
    this.selectionChanged.emit(this.selectedValue);
  }

  /**
   * Selects the option matching the given value; does nothing if no option matches.
   * Kept for API compatibility with callers that select by value.
   * @param value The value of the option to select.
   */
  selectValue(value: string) {
    const item = (this.options ?? []).find((option: any) => option[this.valueKey] == value);
    if (item) {
      this.selectItem(item);
    }
  }

  /**
   * Commits the typed text as a new entry and emits `{ value: '', label, isCreated: true }`.
   * Ignored when the search term is blank.
   */
  createAndSelectValue() {
    const createdLabel = this.searchTerm.trim();
    if (!createdLabel) return;
    this.selectedValue = '';
    this.searchTerm = createdLabel;
    this.closePanel();
    this.selectionChanged.emit({ value: '', label: createdLabel, isCreated: true });
  }

  /** Closes the list without changing the selection and restores the committed label. */
  cancel() {
    this.searchTerm = this.getSelectedLabel();
    this.closePanel();
  }

  /**
   * Dismisses the panel without selecting and emits `null`.
   * Kept for API compatibility with callers of the previous modal.
   */
  close() {
    this.cancel();
    this.selectionChanged.emit(null);
  }

  /** Closes the suggestion panel and removes focus from the input (dismissing the mobile keyboard). */
  private closePanel() {
    this.isOpen = false;
    this.hasUnselectedInput = false;
    this.inputRef?.nativeElement.blur();
  }

  /**
   * Resolves the label to show in the input: `displayValue` if set, otherwise the label of the
   * option matching `selectedValue`, or an empty string when nothing matches.
   */
  private getSelectedLabel(): string {
    if (this.displayValue) return this.displayValue;
    if (this.selectedValue === null || this.selectedValue === undefined || this.selectedValue === '') return '';
    const match = (this.options ?? []).find((item: any) => item[this.valueKey] == this.selectedValue);
    return match?.[this.labelKey]?.toString() ?? '';
  }

  /**
   * Sizes and positions the list (`position: fixed`, so scrollable form containers never clip it).
   * - Height follows the space actually visible between the field and the screen edge, which on
   *   mobile excludes the on-screen keyboard, up to `MAX_PANEL_HEIGHT`.
   * - Prefers `popupDirection` and flips when that side is below the minimum height and the other
   *   side has more room.
   * - A 'top' panel is anchored at its bottom edge and grows upward via CSS (`translateY(-100%)`),
   *   so its height can follow its content without depending on the window height.
   */
  private updatePanelPosition() {
    const field = this.fieldRef?.nativeElement;
    if (!field) return;

    const rect = field.getBoundingClientRect();
    const container = this.getFixedContainingBlock(field);
    const area = this.getVisibleArea(container.clip);
    const spaceAbove = rect.top - PANEL_GAP - (area.top + VIEWPORT_MARGIN);
    const spaceBelow = (area.bottom - VIEWPORT_MARGIN) - (rect.bottom + PANEL_GAP);

    let placement = this.popupDirection;
    if (placement === 'top' && spaceAbove < MIN_PANEL_HEIGHT && spaceBelow > spaceAbove) {
      placement = 'bottom';
    } else if (placement === 'bottom' && spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow) {
      placement = 'top';
    }
    this.placement = placement;

    // Use the space on the chosen side, up to the maximum. If neither side fits the minimum (tiny screen
    // with the keyboard open), this is the larger side: the panel must never cover the input being typed in.
    const available = Math.max(placement === 'top' ? spaceAbove : spaceBelow, 0);
    const maxHeight = Math.min(available, MAX_PANEL_HEIGHT);
    const anchorY = placement === 'top' ? rect.top - PANEL_GAP : rect.bottom + PANEL_GAP;

    const width = Math.min(rect.width, area.right - area.left);
    const left = Math.min(Math.max(rect.left, area.left), area.right - width);

    this.panelStyle = {
      top: `${anchorY - container.originTop}px`,
      left: `${left - container.originLeft}px`,
      width: `${width}px`,
      maxHeight: `${maxHeight}px`
    };
  }

  /**
   * Returns the screen area that is actually visible, in viewport coordinates.
   * @param clip Optional rect (e.g. a transformed chat container) the panel must also stay within.
   */
  private getVisibleArea(clip: DOMRect | null): ScreenArea {
    const area = this.getViewportArea();
    if (clip) {
      area.top = Math.max(area.top, clip.top);
      area.bottom = Math.min(area.bottom, clip.bottom);
      area.left = Math.max(area.left, clip.left);
      area.right = Math.min(area.right, clip.right);
    }
    return area;
  }

  /**
   * On mobile the visual viewport excludes the on-screen keyboard and may be panned (iOS keeps the
   * layout viewport full height and scrolls it), so it is preferred over the window size.
   */
  private getViewportArea(): ScreenArea {
    const viewport = window.visualViewport;
    return viewport
      ? {
        top: viewport.offsetTop,
        bottom: viewport.offsetTop + viewport.height,
        left: viewport.offsetLeft,
        right: viewport.offsetLeft + viewport.width
      }
      : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
  }

  /**
   * A transformed/filtered ancestor becomes the containing block for `position: fixed`
   * (e.g. the chat widget's translateY animation), so offsets must be relative to its padding box
   * and the panel must stay within its bounds.
   */
  private getFixedContainingBlock(element: HTMLElement): { originLeft: number; originTop: number; clip: DOMRect | null } {
    let ancestor = element.parentElement;
    while (ancestor && ancestor !== document.body) {
      const style = getComputedStyle(ancestor);
      if (style.transform !== 'none' || style.filter !== 'none' || style.perspective !== 'none'
        || /transform|filter|perspective/.test(style.willChange) || /paint|layout|strict|content/.test(style.contain)) {
        const rect = ancestor.getBoundingClientRect();
        return {
          originLeft: rect.left + ancestor.clientLeft,
          originTop: rect.top + ancestor.clientTop,
          clip: rect
        };
      }
      ancestor = ancestor.parentElement;
    }
    return { originLeft: 0, originTop: 0, clip: null };
  }
}
